import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';
import {
  monolithTracePropagationTargets,
  sampleRate,
  scrubBreadcrumb,
  scrubLog,
  scrubSpan,
  scrubUrlsIntegration,
} from 'sentry-privacy';

import { domainMeta } from './domainMeta';
import { isReloadedChunkError } from './reloadOnChunkLoadError';

// What each engine says when a fetch fails at the network layer: WebKit,
// Chromium, Gecko. The SDK appends the request host in parentheses.
const FETCH_NETWORK_ERROR =
  /^(?:Load failed|Failed to fetch|NetworkError when attempting to fetch resource\.)(?: \(.*\))?$/;

/** Whether a fetch failed at the network layer, before any response. */
export const isFetchNetworkError = (error: unknown): boolean =>
  error instanceof TypeError && FETCH_NETWORK_ERROR.test(error.message);

/**
 * Drops a fetch network failure that carries no stack frame.
 *
 * The SDK stamps each fetch it wraps with its caller's stack, so a request this
 * bundle sends fails with this bundle's frames and still reports. A failure
 * with no frame at all came from a caller that has no script URL, such as a
 * script an in-app browser injects into the page. Those events say only that
 * the candidate's network dropped, and name no code to fix.
 */
export function dropFramelessNetworkError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];

  if (
    exception?.type === 'TypeError' &&
    FETCH_NETWORK_ERROR.test(exception.value ?? '') &&
    !exception.stacktrace?.frames?.length
  ) {
    return null;
  }

  return event;
}

// WebKit's message for a script reading across a frame boundary.
const CROSS_ORIGIN_FRAME_ERROR =
  /^Blocked a frame with origin "[^"]*" from accessing a cross-origin frame\./;

// A bundle file's URL, as opposed to the page URL an inline script reports.
const SCRIPT_FILE = /^[^?#\s]*\.[cm]?js(?:[?#]|$)/;

// A file of this app's build, which every deploy serves under `/apply/<sha7>/`.
const BUNDLE_FILE =
  /^[^?#\s]*\/apply\/[0-9a-f]{7}\/[^?#\s]*\.[cm]?js(?:[?#]|$)/;

/**
 * Drops a cross-origin frame access that no bundle file took part in.
 *
 * The served page holds no inline script, so a frame whose file is the page
 * URL belongs to a script something else put there: an account's tracker or
 * landing snippet, or a script the browser injects. Such an error names no
 * code this app can fix. A frame from a bundle file keeps the event, so this
 * app's own cross-frame access still reports.
 */
export function dropInlineCrossOriginFrameError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];
  const frames = exception?.stacktrace?.frames ?? [];

  if (
    exception?.type === 'SecurityError' &&
    CROSS_ORIGIN_FRAME_ERROR.test(exception.value ?? '') &&
    !frames.some(frame => SCRIPT_FILE.test(frame.filename ?? ''))
  ) {
    return null;
  }

  return event;
}

// How the SDK reports the `AbortError` DOMException a `pause()` raises in a
// pending `play()`: WebKit gives the engine's default message, Chromium names
// the interrupted call.
const PLAY_INTERRUPTED_ABORT =
  /^AbortError: (?:The operation was aborted\.$|The play\(\) request was interrupted by a call to pause\(\))/;

/**
 * Drops a stackless play abort while the CameraTag recorder is on the page.
 *
 * CameraTag's script, loaded from cameratag.com by the video recording stage,
 * calls `play()` on its preview video without handling the promise. Its own
 * `pause()` interrupts a pending `play()`, and the browser rejects that promise
 * with a stackless `AbortError`. The event names no frame and no code this app
 * can fix. The same abort on a page without CameraTag still reports.
 */
export function dropCameraTagAbortError(event: ErrorEvent): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];

  if (
    exception &&
    PLAY_INTERRUPTED_ABORT.test(exception.value ?? '') &&
    !exception.stacktrace?.frames?.length &&
    window.CameraTag
  ) {
    return null;
  }

  return event;
}

// What WebKit, then Chromium and Gecko, say when a script calls the Facebook
// pixel's global before the pixel defines it.
const MISSING_FACEBOOK_PIXEL =
  /^(?:Can't find variable: fbq|fbq is not defined)$/;

/**
 * Drops a call to an undefined Facebook pixel made by an inline script.
 *
 * An account's custom conversion tracker runs as an inline script, so its
 * frame is the page URL. A tracker that calls `fbq` without loading the pixel
 * first throws this on every page it runs on, and names no code this app can
 * fix. The built-in Facebook tracker defines `fbq` before it calls it. An
 * innermost frame from a bundle file keeps the event.
 */
export function dropInlineMissingFacebookPixelError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];
  const frames = exception?.stacktrace?.frames ?? [];
  const innermost = frames[frames.length - 1];

  if (
    exception?.type === 'ReferenceError' &&
    MISSING_FACEBOOK_PIXEL.test(exception.value ?? '') &&
    !SCRIPT_FILE.test(innermost?.filename ?? '')
  ) {
    return null;
  }

  return event;
}

// What WebKit, then Chromium, say when a script calls an insertAdjacent method
// on an element lookup that found nothing.
const INSERT_ADJACENT_ON_NULL =
  /^(?:null is not an object \(evaluating '.*\.insertAdjacent(?:Element|HTML|Text)'\)|Cannot read properties of null \(reading 'insertAdjacent(?:Element|HTML|Text)'\))$/;

/**
 * Drops an inline script's insert next to an element the page has not rendered.
 *
 * An account's stage JavaScript runs inline once the stage mounts, so its frame
 * is the page URL, or `<anonymous>` in Chromium. A script that anchors content
 * to a form field throws this whenever that field is not on screen yet: behind
 * the stage's landing screen, or on a later question of a one-at-a-time form.
 * It names no code this app can fix. An innermost frame from a bundle file
 * keeps the event.
 */
export function dropInlineInsertAdjacentOnNullError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];
  const frames = exception?.stacktrace?.frames ?? [];
  const innermost = frames[frames.length - 1];

  if (
    exception?.type === 'TypeError' &&
    INSERT_ADJACENT_ON_NULL.test(exception.value ?? '') &&
    !SCRIPT_FILE.test(innermost?.filename ?? '')
  ) {
    return null;
  }

  return event;
}

// Transcend's consent script, served as `/cm/<bundle id>/airgap.js`.
const TRANSCEND_AIRGAP = /\/airgap\.js(?:[?#]|$)/;

// The file the SDK names for a frame inside a built-in function in WebKit.
const NATIVE_FRAME = '[native code]';

/**
 * Drops an error thrown when only Transcend's airgap script ran.
 *
 * On load airgap reads and clears the cookies the visitor has not consented
 * to, and in some browsers that code throws on its own: WebKit rejects its
 * `cookieStore.getAll()` with a messageless `TypeError`, its cookie-domain
 * lookup reads an undefined value, and Chromium throws `Illegal invocation`
 * from a native method it wrapped. Every frame of such an error is airgap's or
 * a built-in's, and it names no code this app can fix. Any other frame keeps
 * the event: a bundle frame, the SDK's fetch instrumentation among them, is a
 * call this app made through airgap's wrappers, and a page-URL frame is an
 * account's inline script.
 */
export function dropTranscendAirgapError(event: ErrorEvent): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];
  const filenames = (exception?.stacktrace?.frames ?? []).map(
    frame => frame.filename ?? '',
  );

  if (
    filenames.some(filename => TRANSCEND_AIRGAP.test(filename)) &&
    filenames.every(
      filename => filename === NATIVE_FRAME || TRANSCEND_AIRGAP.test(filename),
    )
  ) {
    return null;
  }

  return event;
}

// A file a browser extension serves, such as `chrome-extension://<id>/x.js`.
const EXTENSION_FRAME = /^[a-z]+-extension:\/\//;

/**
 * Drops a fetch network failure that code outside this app's build requested.
 *
 * An account's custom JavaScript, which the bundle appends inline, and the
 * tracker files such scripts load from other hosts, fetch from hosts some
 * candidates cannot reach and attach no `catch`. The frame that called `fetch`
 * is then inline code, named by the page URL or `<anonymous>`, or a file on
 * another host. Chromium reports the SDK's fetch wrapper, a bundle frame named
 * `fetch`, inside that caller; WebKit drops it, so its innermost frame is the
 * caller itself. A browser extension that wraps `fetch` puts its frames
 * between the caller and the wrapper, so they are skipped. A caller from this
 * app's build keeps the event, and so does Transcend's airgap, whose fetch
 * wrapper issues this app's own requests from its own frames.
 */
export function dropForeignFetchNetworkError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];
  const frames = (exception?.stacktrace?.frames ?? []).filter(
    frame => !EXTENSION_FRAME.test(frame.filename ?? ''),
  );
  const innermost = frames[frames.length - 1];
  const caller =
    innermost?.function === 'fetch' &&
    BUNDLE_FILE.test(innermost.filename ?? '')
      ? frames[frames.length - 2]
      : innermost;
  const callerFile = caller?.filename ?? '';

  if (
    exception?.type === 'TypeError' &&
    FETCH_NETWORK_ERROR.test(exception.value ?? '') &&
    caller?.lineno !== undefined &&
    callerFile !== NATIVE_FRAME &&
    !BUNDLE_FILE.test(callerFile) &&
    !TRANSCEND_AIRGAP.test(callerFile)
  ) {
    return null;
  }

  return event;
}

/**
 * Drops a module preload that failed at the network layer.
 *
 * On a browser without native `modulepreload`, the polyfill Vite puts in the
 * entry chunk fetches each preloaded chunk only to warm the cache, and never
 * catches that `fetch`. The `import()` that needs the chunk requests it again
 * and reports its own failure, so this event either repeats that report or
 * names a request nothing waited on. The same failure from any other function
 * still reports.
 */
export function dropModulePreloadNetworkError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];
  const frames = exception?.stacktrace?.frames ?? [];
  const innermost = frames[frames.length - 1];

  if (
    exception?.type === 'TypeError' &&
    FETCH_NETWORK_ERROR.test(exception.value ?? '') &&
    innermost?.function === 'processPreload' &&
    BUNDLE_FILE.test(innermost.filename ?? '')
  ) {
    return null;
  }

  return event;
}

// How the SDK reports a promise rejected with no value.
const UNDEFINED_REJECTION =
  'Non-Error promise rejection captured with value: undefined';

/**
 * Drops an unhandled promise rejection whose reason is `undefined`.
 *
 * Such a rejection carries no message and no stack, so the event names no
 * code to fix. This app's own code never rejects with nothing.
 */
export function dropUndefinedRejection(event: ErrorEvent): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];

  if (
    exception?.type === 'UnhandledRejection' &&
    exception.value === UNDEFINED_REJECTION &&
    !exception.stacktrace?.frames?.length
  ) {
    return null;
  }

  return event;
}

// What WebKit, then Chromium, say when the call stack overflows.
const STACK_OVERFLOW = /^Maximum call stack size exceeded\.?$/;

/**
 * Drops a stack overflow that no bundle file took part in.
 *
 * Chrome for iOS and the Google app inject a script into every page they
 * load, and its recursion overflows the stack on this one. Its frames report
 * the URL of the page it was injected into, which stays the first page the
 * candidate opened after the app routes elsewhere. It names no code this app
 * can fix. A frame from a bundle file keeps the event, so a recursion in this
 * app's code still reports.
 */
export function dropInlineStackOverflowError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];
  const frames = exception?.stacktrace?.frames ?? [];

  if (
    exception?.type === 'RangeError' &&
    STACK_OVERFLOW.test(exception.value ?? '') &&
    frames.length > 0 &&
    !frames.some(frame => SCRIPT_FILE.test(frame.filename ?? ''))
  ) {
    return null;
  }

  return event;
}

/**
 * Drops an error thrown by code that no script file holds.
 *
 * The served page holds no inline script, so a frame with a line number in a
 * file that is not a script file runs code something else put on the page:
 * an account's conversion tracker or stage script, which the bundle appends
 * inline, or a script the browser injects. WebKit and Gecko name such a frame
 * by the page URL, Chromium by `<anonymous>`. When the innermost frame is one,
 * the error names code this app cannot fix. WebKit reports a built-in such
 * as `postMessage` as a frame of its own inside its caller, so the test skips
 * those and reads the code that called the built-in. A built-in reports no
 * line number, so a bundle call that fails inside `JSON.parse` still reports.
 */
export function dropInlineScriptError(event: ErrorEvent): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];
  const frames = exception?.stacktrace?.frames ?? [];
  const innermost = frames
    .filter(frame => frame.filename !== NATIVE_FRAME)
    .pop();

  if (
    innermost?.lineno !== undefined &&
    !SCRIPT_FILE.test(innermost.filename ?? '')
  ) {
    return null;
  }

  return event;
}

// Chromium prefixes a script's parse error with the DOM call that inserted it.
const INSERTED_SCRIPT = /^Failed to execute '[^']*' on 'Node': /;

/**
 * Drops a parse error in a script the page inserted.
 *
 * The bundle inserts only account scripts, the conversion trackers and stage
 * JavaScript, and an account that pastes a snippet with smart quotes or a
 * missing parenthesis fails to parse on every page it runs on. Chromium names
 * the inserting call in the message; WebKit reports the native `appendChild`
 * as the innermost frame. The bundle itself parses at load, so a
 * `JSON.parse` or `RegExp` SyntaxError in this app's code still reports.
 */
export function dropInsertedScriptSyntaxError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];
  const frames = exception?.stacktrace?.frames ?? [];
  const innermost = frames[frames.length - 1];

  if (
    exception?.type === 'SyntaxError' &&
    (INSERTED_SCRIPT.test(exception.value ?? '') ||
      (innermost?.filename === NATIVE_FRAME &&
        innermost.function === 'appendChild'))
  ) {
    return null;
  }

  return event;
}

// The namespace Firefox for iOS, and Brave for iOS built from it, gives the
// scripts it injects into every page.
const IOS_BROWSER_USER_SCRIPT = /__firefox__/;

/**
 * Drops an error that names that namespace when no bundle file took part in it.
 *
 * Brave for iOS injects scripts that read `window.__firefox__`, and on this
 * page they can run before the browser defines it. They report the page URL as
 * their file. Such an error names no code this app can fix. A frame from a
 * bundle file keeps the event.
 */
export function dropIosBrowserUserScriptError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];
  const frames = exception?.stacktrace?.frames ?? [];

  if (
    IOS_BROWSER_USER_SCRIPT.test(exception?.value ?? '') &&
    !frames.some(frame => SCRIPT_FILE.test(frame.filename ?? ''))
  ) {
    return null;
  }

  return event;
}

// What Chromium, Gecko and WebKit say when a script declares a global `let`,
// `const` or `class` that an earlier script on the page already declared.
// Chromium prefixes the DOM call that ran the script, such as appendChild.
const REDECLARATION =
  /^(?:Failed to execute '[^']*' on '[^']*': )?(?:Identifier '[^']*' has already been declared|redeclaration of |Can't create duplicate variable)/;

/**
 * Drops a script's redeclaration of a global `let`, `const` or `class`.
 *
 * Conversion trackers remount on a stage change or a refetch, and the globals
 * an account's tracker declared outlive the mount, so the rerun throws before
 * any of it runs. The bundle cannot raise this at runtime: its modules fail at
 * build time. Every such event names account code this app cannot fix.
 */
export function dropAccountScriptRedeclarationError(
  event: ErrorEvent,
): ErrorEvent | null {
  const values = event.exception?.values ?? [];
  const exception = values[values.length - 1];

  if (
    exception?.type === 'SyntaxError' &&
    REDECLARATION.test(exception.value ?? '')
  ) {
    return null;
  }

  return event;
}

/**
 * Browser error reporting, tracing, profiling and logs for the applicant
 * portal.
 *
 * No session replay. The page URL here is the applicant's credential, and a
 * replay records it where nothing can scrub it: the rrweb meta event that opens
 * every snapshot carries `window.location.href` and reaches no SDK hook, and
 * the DOM snapshot keeps every portal link's `href`. Errors and traces still
 * say what broke, and `applicant.ref` says whose session it was.
 *
 * Dormant unless SENTRY_DSN is baked into the build. That gate is the whole
 * switch: a local `npm run start`, a Storybook build and a Jest run all leave it
 * unset and therefore start no client at all.
 *
 * One bundle serves every tenant, so the DSN is a build-time constant while the
 * environment and tenant are read from the hostname at load.
 */

export function initSentry(): void {
  const dsn = process.env.SENTRY_DSN;

  if (!dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: domainMeta.environment,
    // Ties an event to the build that produced it, and to the source maps
    // uploaded under the same name.
    release: process.env.COMMIT_SHA,
    tracesSampleRate: sampleRate(process.env.SENTRY_TRACES_SAMPLE_RATE, 0.01),
    // Relative to the sampled traces, not to all page loads. Inert until the
    // frontend worker serves `Document-Policy: js-profiling`.
    profilesSampleRate: sampleRate(process.env.SENTRY_PROFILES_SAMPLE_RATE, 1),
    tracePropagationTargets: monolithTracePropagationTargets(
      domainMeta.monolithOrigin,
      Sentry.getActiveSpan,
    ),
    // `scrubBreadcrumb` drops console breadcrumbs, and `scrubLog` keeps a
    // console log's text but not its arguments: what the app logs includes
    // `ApiError.body`, which is what a candidate typed. On unless the build
    // sets the variable to "false"; turning it off takes a rebuild.
    enableLogs: process.env.SENTRY_LOGS_ENABLED !== 'false',
    beforeSendLog: scrubLog,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      // Fed by `utils/sentryUser`. The flags on an event are the ones the
      // session was evaluated under, which is usually the first question asked
      // of an issue that only some accounts see.
      Sentry.featureFlagsIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      // Page and request URLs carry credentials and search terms.
      scrubUrlsIntegration(),
    ],
    beforeBreadcrumb: scrubBreadcrumb,
    // Standalone spans, INP among them, reach no event processor.
    beforeSendSpan: scrubSpan,
    // These pages carry candidate PII. Nothing about a candidate reaches Sentry
    // that this app does not put there deliberately.
    sendDefaultPii: false,
    // Transcend's consent scripts fetch from their own hosts, and a blocked or
    // cancelled request rejects inside their code. The SDK appends the failed
    // host to the message, and each release groups the frames anew, so these
    // fill the project with copies of one cause this app cannot fix.
    // Their consent UI also rejects without a host when a translation file
    // fails to load. Filter by message, not by script URL: Transcend's airgap
    // wraps native DOM and network methods, so a script-URL filter would also
    // drop errors this app's own code raises through those wrappers.
    ignoreErrors: [
      /\((?:transcend-cdn\.com|telemetry\.transcend\.io)\)$/,
      /^Failed to load translations for language /,
      // Adobe's Web SDK, loaded by an account's Adobe Launch tag, rejects with
      // this when the browser blocks its identity request, as Firefox's
      // tracking protection does. This app loads no Adobe code.
      /^No identity was set on response\.$/,
      // The link scanner behind Outlook Safe Links and Microsoft Defender
      // rejects with this when it opens a link from a candidate's email.
      /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/,
      // WebKit rejects a Safari web extension's message from a tab it cannot
      // find. This app calls no extension API.
      /^Invalid call to runtime\.sendMessage\(\)\. Tab not found\.$/,
      // A promise rejected with a resource's load `error` event, such as a
      // script loader's `onerror = reject`. The event carries no message, no
      // stack and no URL, so it names nothing to fix. This app's only such
      // loader, the consent script's, catches its rejection.
      /^Event `Event` \(type=error\) captured as promise rejection$/,
    ],
    beforeSend: (event, hint) => {
      // The page is already reloading to fetch the chunk again.
      if (isReloadedChunkError(hint.originalException)) return null;
      const ownScript = dropAccountScriptRedeclarationError(event);
      const kept = ownScript && dropFramelessNetworkError(ownScript);
      const ownFetch = kept && dropForeignFetchNetworkError(kept);
      const noPreload = ownFetch && dropModulePreloadNetworkError(ownFetch);
      const ownFrame = noPreload && dropInlineCrossOriginFrameError(noPreload);
      const noCameraTag = ownFrame && dropCameraTagAbortError(ownFrame);
      const noPixel =
        noCameraTag && dropInlineMissingFacebookPixelError(noCameraTag);
      const noUserScript = noPixel && dropIosBrowserUserScriptError(noPixel);
      const noAnchor =
        noUserScript && dropInlineInsertAdjacentOnNullError(noUserScript);
      const noAirgap = noAnchor && dropTranscendAirgapError(noAnchor);
      const noOverflow = noAirgap && dropInlineStackOverflowError(noAirgap);
      const noInline = noOverflow && dropInlineScriptError(noOverflow);
      const parsed = noInline && dropInsertedScriptSyntaxError(noInline);
      return parsed && dropUndefinedRejection(parsed);
    },
  });

  Sentry.setTag('tenant', domainMeta.tenant);
  // A random id per page load, so an issue counts the people it reaches and a
  // users-affected alert can fire. It is kept in memory only and names no
  // account, applicant or browser.
  Sentry.setUser({ id: Math.random().toString(36).slice(2) });
}
